import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { VeNewsFeed } from '../../typings';
import { ActivityLoggerService } from '../../services/activityLogger.service';

// YouTube Player States
enum YouTubePlayerState {
  ENDED = 0,
  PAUSED = 2,
  PLAYING = 1,
  BUFFERING = 3,
  CUED = 5,
  UNSTARTED = -1
}

// Error types pour un meilleur tracking
enum VideoPlayerError {
  LOADING_FAILED = 'LOADING_FAILED',
  PLAYBACK_FAILED = 'PLAYBACK_FAILED',
  API_ERROR = 'API_ERROR',
  TIMEOUT = 'TIMEOUT'
}

declare let YT: any;

// Configuration améliorée
const RETRY_INTERVAL_MS = 200;
const MAX_RETRY_ATTEMPTS = 25;
const VIDEO_TIMEOUT_MS = 8000; // 8 secondes timeout
const BUFFERING_TIMEOUT_MS = 10000; // 10 secondes pour le buffering

@Component({
  selector: 'app-videoplayer',
  templateUrl: './videoplayer.component.html',
  styleUrl: './videoplayer.component.scss',
  standalone: false
}) 
export class VideoplayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentNews: VeNewsFeed;
  @Input() isMobile: boolean = false;
  @Output() finished = new EventEmitter<boolean>();
  @Output() videoPlayerError = new EventEmitter<VideoPlayerError>(); // Nouvel événement d'erreur

  public player: any;
  private isVideoComplete = false;
  private retryInterval: number | null = null;
  private retryCount = 0;
  private videoTimeoutInterval: number | null = null;
  private bufferingTimeoutInterval: number | null = null;
  private lastStateChange = Date.now();
  private isPlayerReady = false;
  private hasVideoStarted = false;

  constructor(public logService: ActivityLoggerService) { 
  }

  ngOnInit(): void {
    console.log('Initializing video player for video');
    this.initializeYouTubePlayer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hasNewsChanged(changes)) {
      this.resetPlayerState();
      this.updateVideo(changes['currentNews'].currentValue.videoUrl);
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private hasNewsChanged(changes: SimpleChanges): boolean {
    console.log('Checking if news has changed:', changes);
    return changes['currentNews'] && 
           changes['currentNews'].previousValue !== changes['currentNews'].currentValue;
  }

  private resetPlayerState(): void {
    this.isVideoComplete = false;
    this.retryCount = 0;
    this.hasVideoStarted = false;
    this.clearAllIntervals();
  }

  private initializeYouTubePlayer(): void {
    try {
      this.player = new YT.Player('player', {
        videoId: this.extractYouTubeVideoId(this.currentNews.videoUrl),
        height: "100%",
        width: "100%",
        host: 'https://www.youtube.com',
        playerVars: this.getPlayerConfig(),
        events: {
          'onReady': this.onPlayerReady.bind(this),
          'onStateChange': this.onPlayerStateChange.bind(this),
          'onError': this.onPlayerError.bind(this) // Gestion des erreurs YouTube
        }
      });
      
      // Timeout global pour l'initialisation
      this.setVideoTimeout();
    } catch (error) {
      console.error('Failed to initialize YouTube player:', error);
      this.handleError(VideoPlayerError.API_ERROR);
    }
  }

  private getPlayerConfig(): any {
    return {
      autoplay: 1,
      controls: 0,
      showinfo: 0,
      modestbranding: 1,
      mute: 1,
      loop: 0,
      rel: 0,
      enablejsapi: 1,
    };
  }

  private onPlayerReady(event: any): void {
    console.log('Player ready');
    this.isPlayerReady = true;
    this.clearRetryInterval();
    this.startVideoPlayback();
  }

  private onPlayerStateChange(event: any): void {
    this.lastStateChange = Date.now();
    console.log('Player state changed:', event.data);
    
    switch (event.data) {
      case YouTubePlayerState.PLAYING:
        this.handleVideoPlaying();
        break;
      case YouTubePlayerState.ENDED:
        this.handleVideoEnded();
        break;
      case YouTubePlayerState.PAUSED:
        this.handleVideoPaused();
        break;
      case YouTubePlayerState.BUFFERING:
        this.handleVideoBuffering();
        break;
      case YouTubePlayerState.UNSTARTED:
        this.handleVideoUnstarted();
        break;
      case YouTubePlayerState.CUED:
        this.handleVideoCued();
        break;
    }
  }

  private onPlayerError(event: any): void {
    console.error('YouTube player error:', event.data);
    this.handleError(VideoPlayerError.API_ERROR);
  }

  private handleVideoPlaying(): void {
    this.hasVideoStarted = true;
    this.isVideoComplete = true;
    this.clearAllIntervals();
    console.log('Video is playing successfully');
  }

  private handleVideoEnded(): void {
      console.log('Video ended normally');
      this.emitVideoFinished();
  }

  private handleVideoPaused(): void {
    if (!this.hasVideoStarted && this.retryCount < MAX_RETRY_ATTEMPTS) {
      console.log('Video paused before starting - retrying');
      this.retryVideoPlayback();
    }
  }

  private handleVideoBuffering(): void {
    console.log('Video is buffering');
    this.setBufferingTimeout();
  }

  private handleVideoUnstarted(): void {
    if (this.retryCount < MAX_RETRY_ATTEMPTS) {
      console.log('Video unstarted - retrying');
      this.retryVideoPlayback();
    } else {
      this.handleError(VideoPlayerError.LOADING_FAILED);
    }
  }

  private handleVideoCued(): void {
    if (!this.hasVideoStarted && this.retryCount < MAX_RETRY_ATTEMPTS) {
      console.log('Video cued - attempting to play');
      this.retryVideoPlayback();
    }
  }

  private handleError(errorType: VideoPlayerError): void {
    console.error(`Video player error: ${errorType}`);
    this.logService.logError('Youtube Error',`${errorType}`);
    this.cleanup();
    this.videoPlayerError.emit(errorType);
    // Fallback: émettre finished après un délai
    setTimeout(() => {
      this.emitVideoFinished();
    }, 1000);
  }

  private emitVideoFinished(): void {
    console.log('Emitting video finished');
    this.cleanup();
    this.finished.emit(true);
    this.resetPlayerState();
  }

  private startVideoPlayback(): void {
    if (!this.player) return;
    
    this.retryInterval = window.setInterval(() => {
      try {
        if (this.player && this.isPlayerReady) {
          this.player.mute();
          this.player.playVideo();
          this.player.mute();
        }
      } catch (error) {
        console.log(this.player);
        console.error('Error starting playback:', error);
         this.clearRetryInterval();
         this.retryVideoPlayback();
      }
    }, RETRY_INTERVAL_MS);
  }

  private retryVideoPlayback(): void {
    if (this.retryCount >= MAX_RETRY_ATTEMPTS) {
      console.error('Max retry attempts reached');
      this.handleError(VideoPlayerError.TIMEOUT);
      return;
    }

    this.clearRetryInterval();
    this.retryCount++;
    console.log(`Retry attempt ${this.retryCount}/${MAX_RETRY_ATTEMPTS}`);
    
    this.retryInterval = window.setInterval(() => {
      try {
        if (this.player && this.isPlayerReady) {
          this.player.mute();
          this.player.playVideo();
          this.player.mute();
        }
      } catch (error) {
        console.error('Error during retry:', error);
         this.clearRetryInterval();
         this.retryVideoPlayback();
      }
    }, RETRY_INTERVAL_MS);
  }

  private setVideoTimeout(): void {
    this.videoTimeoutInterval = window.setTimeout(() => {
      if (!this.hasVideoStarted) {
        console.error('Video timeout - no playback started');
        this.handleError(VideoPlayerError.TIMEOUT);
      }
    }, VIDEO_TIMEOUT_MS);
  }

  private setBufferingTimeout(): void {
    this.clearBufferingTimeout();
    this.bufferingTimeoutInterval = window.setTimeout(() => {
      if (!this.hasVideoStarted) {
        console.error('Buffering timeout - video never started');
        this.handleError(VideoPlayerError.TIMEOUT);
      } else {
        // Mid-playback buffering timeout: network may have dropped
        console.warn('Mid-playback buffering timeout - attempting recovery');
        this.attemptPlaybackRecovery();
      }
    }, BUFFERING_TIMEOUT_MS);
  }

  private attemptPlaybackRecovery(): void {
    if (this.player && this.isPlayerReady) {
      try {
        const currentTime = this.player.getCurrentTime();
        console.log(`Seeking to ${currentTime} to force resume`);
        this.player.seekTo(currentTime, true);
        // Give the player a few seconds to resume; if still not playing, move on
        this.bufferingTimeoutInterval = window.setTimeout(() => {
          try {
            const state = this.player?.getPlayerState();
            if (state !== YouTubePlayerState.PLAYING) {
              console.error('Recovery failed - moving to next news');
              this.handleError(VideoPlayerError.TIMEOUT);
            }
          } catch {
            this.handleError(VideoPlayerError.TIMEOUT);
          }
        }, 5000);
      } catch (error) {
        console.error('Recovery attempt failed:', error);
        this.handleError(VideoPlayerError.TIMEOUT);
      }
    } else {
      this.handleError(VideoPlayerError.TIMEOUT);
    }
  }

  private clearRetryInterval(): void {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  private clearVideoTimeout(): void {
    if (this.videoTimeoutInterval) {
      clearTimeout(this.videoTimeoutInterval);
      this.videoTimeoutInterval = null;
    }
  }

  private clearBufferingTimeout(): void {
    if (this.bufferingTimeoutInterval) {
      clearTimeout(this.bufferingTimeoutInterval);
      this.bufferingTimeoutInterval = null;
    }
  }

  private clearAllIntervals(): void {
    this.clearRetryInterval();
    this.clearVideoTimeout();
    this.clearBufferingTimeout();
  }

  private cleanup(): void {
    this.clearAllIntervals();
    if (this.player) {
      try {
        // Arrêter la vidéo avant de détruire
        if (typeof this.player.stopVideo === 'function') {
          this.player.stopVideo();
        }
        
        // Détruire le player
        if (typeof this.player.destroy === 'function') {
          this.player.destroy();
        }
        
        // Nettoyer l'élément DOM
        let playerElement = document.getElementById('player');
        if (playerElement) {
          playerElement.innerHTML = '';
        }
        
      } catch (error) {
        console.error('Error destroying player:', error);
      } finally {
        this.player = null;
      }
    }
    // Reset de tous les états
    this.resetPlayerState();
  }

  private updateVideo(videoUrl: string): void {
    const videoId = this.extractYouTubeVideoId(videoUrl);
    if (videoId) {
      try {
        if (this.player && this.isPlayerReady) {
          this.player.loadVideoById(videoId);
          this.setVideoTimeout();
        } else {
          this.initializeYouTubePlayer();
        }
      } catch (error) {
        console.error('Error updating video:', error);
        this.initializeYouTubePlayer();
      }
    } else {
      this.handleError(VideoPlayerError.LOADING_FAILED);
    }
  }

  private extractYouTubeVideoId(url: string): string | null {
    console.log('Extracting video ID from URL:', url);
    if (!url) {
      return null;
    }

    // Nettoyer l'URL et supprimer les espaces
    const cleanUrl = url.trim();

    // Patterns pour différents formats YouTube
    const patterns = [
      // Format standard: https://www.youtube.com/watch?v=VIDEO_ID
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
      
      // Format court: https://youtu.be/VIDEO_ID
      /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      
      // Format embed: https://www.youtube.com/embed/VIDEO_ID
      /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      
      // Format shorts: https://www.youtube.com/shorts/VIDEO_ID
      /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      
      // Format avec timestamp: https://www.youtube.com/watch?v=VIDEO_ID&t=123s
      /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
      
      // Format mobile: https://m.youtube.com/watch?v=VIDEO_ID
      /(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
      
      // Format avec sous-domaine: https://music.youtube.com/watch?v=VIDEO_ID
      /(?:music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
      
      // Format nocookie: https://www.youtube-nocookie.com/embed/VIDEO_ID
      /(?:youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    ];

    // Tester chaque pattern
    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern);
      if (match && match[1]) {
        console.log('Extracted video ID:', match[1]);
        return match[1];
      }
    }

    // Si aucun pattern ne correspond, vérifier si c'est déjà un ID valide
    const directIdPattern = /^[a-zA-Z0-9_-]{11}$/;
    if (directIdPattern.test(cleanUrl)) {
      return cleanUrl;
    }

    return null;
  }

  // Méthode legacy pour compatibilité (remplace stripYoutubeId)
  private stripYoutubeId(link: string): string | null {
    return this.extractYouTubeVideoId(link);
  }
}